import React, { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({
  question,
  answer,
  isOpen,
  onClick,
}) => (
  <div className="relative w-full bg-white overflow-hidden border border-solid border-nileBlueLight mb-3 rounded-[15px] rounded-b-none">
    <button
      className="relative flex justify-between items-center text-left bg-white top-0 left-0 w-full outline-none text-black focus-visible:bg-shade100 p-5 [&_svg]:text-[#F47B20] [&_svg]:transition-transform [&_svg]:duration-150 [&_svg]:ml-4"
      onClick={onClick}
      aria-expanded={isOpen}
    >
      <span className="text-black leading-[1.56] text-[16px] md:text-[18px] font-normal w-[calc(100%-2rem)]">
        {question}
      </span>
      <Plus
        className={`transition-transform duration-150 ${
          isOpen ? "rotate-45" : ""
        }`}
      />
    </button>
    <div
      className={`transition-all duration-300 ease-in-out ${
        isOpen ? "max-h-[500px]" : "max-h-0"
      } overflow-hidden`}
    >
      <div className="px-5 pb-5 text-base leading-[1.56]">{answer}</div>
    </div>
  </div>
);

function FAQKookgroep() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "Wat is een kookgroep en waarom heb ik die nodig?",
      answer: (
        <>
          Een kookgroep is een speciale groep in de meterkast die voldoende
          stroom levert aan een inductiekookplaat of keramische kookplaat. Deze
          apparaten verbruiken meer stroom dan een standaard groep aankan. Een
          kookgroep voorkomt overbelasting, stroomuitval en kortsluiting en
          zorgt ervoor dat je kookplaat veilig en efficiënt werkt.
        </>
      ),
    },
    {
      question: "Heb ik een 2-fase of 3-fase kookgroep nodig?",
      answer: (
        <>
          Dit hangt af van je inductiekookplaat en je meterkast:
          <br></br>
          <ul className="pl-4">
            <li className="list-disc">
              2-fase kookgroep (230V): Geschikt voor de meeste standaard
              inductiekookplaten.
            </li>
            <li className="list-disc">
              3-fase kookgroep (400V, krachtstroom): Nodig voor krachtigere
              kookplaten met een hoger vermogen.
            </li>
          </ul>
          <br></br>
          Om zeker te weten welke aansluiting je nodig hebt, kun je de
          specificaties van je kookplaat controleren of een elektricien
          inschakelen voor advies.
        </>
      ),
    },
    {
      question: "Hoe weet ik of mijn meterkast geschikt is voor een kookgroep?",
      answer: (
        <>
          Controleer of je meterkast voldoende vrije groepen heeft en of je een
          Perilex-aansluiting hebt. Voor een 3-fase kookgroep moet je meterkast
          geschikt zijn voor krachtstroom (400V). Twijfel je? Laat dan een
          elektricien je groepenkast inspecteren.
        </>
      ),
    },
    {
      question: "Wat kost het installeren van een kookgroep?",
      answer: (
        <>
          De kosten voor het installeren van een kookgroep variëren afhankelijk
          van:
          <br></br>
          <ul className="pl-4">
            <li className="list-disc">
              Of je een 2-fase of 3-fase kookgroep nodig hebt.
            </li>
            <li className="list-disc">
              Of je meterkast moet worden aangepast.
            </li>
            <li className="list-disc">
              De benodigde bekabeling en extra werkzaamheden.
            </li>
          </ul>
          <br></br>
          Voor een prijsindicatie kun je vrijblijvend een offerte aanvragen bij
          Elektra Aanleggen.
        </>
      ),
    },
    {
      question: "Hoe lang duurt het installeren van een kookgroep?",
      answer: (
        <>
          Het plaatsen van een kookgroep duurt gemiddeld 1 tot 3 uur,
          afhankelijk van de situatie. Moet de meterkast worden uitgebreid of
          kabels worden gefreesd? Dan kan het langer duren.
        </>
      ),
    },
    {
      question: "Kan ik zelf een kookgroep installeren?",
      answer: (
        <>
          Het is niet aan te raden om zelf een kookgroep te installeren, tenzij
          je een gecertificeerde elektricien bent. Een onjuiste aansluiting kan
          kortsluiting, storingen en zelfs brandgevaar veroorzaken. Laat dit
          daarom altijd door een erkende elektricien doen.
        </>
      ),
    },
    {
      question: "Wat is een Perilex-aansluiting en heb ik die nodig?",
      answer: (
        <>
          Een Perilex-aansluiting is een speciaal krachtstroom stopcontact voor
          inductiekookplaten. In tegenstelling tot een standaard stopcontact,
          heeft een Perilex-aansluiting meerdere draden om het hogere vermogen
          veilig te verdelen. De meeste inductiekookplaten hebben een
          Perilex-aansluiting nodig.
        </>
      ),
    },
    {
      question:
        "Kan ik een inductiekookplaat aansluiten op een normaal stopcontact?",
      answer: (
        <>
          Nee, een normaal stopcontact kan niet genoeg stroom leveren aan een
          inductiekookplaat. Dit kan leiden tot stroomuitval of schade aan de
          kookplaat. Daarom moet je altijd een geschikte kookgroep en
          Perilex-aansluiting gebruiken.
        </>
      ),
    },
  ];

  return (
    <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1260px] mb-12 md:mb-16">
      <div className="mx-auto md:max-w-[520px] lg:max-w-[100vw]">
        <div className="font-bold text-black leading-[1.25] text-[20px] lg:text-[28px] mb-6">
          Vraag en antwoord
        </div>
        <div className="grid auto-rows-auto lg:gap-x-8 lg:grid-cols-[1fr_1fr]">
          <div>
            {faqs.slice(0, Math.ceil(faqs.length / 2)).map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
          </div>
          <div>
            {faqs.slice(Math.ceil(faqs.length / 2)).map((faq, index) => (
              <FAQItem
                key={index + Math.ceil(faqs.length / 2)}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index + Math.ceil(faqs.length / 2)}
                onClick={() =>
                  setOpenIndex(
                    openIndex === index + Math.ceil(faqs.length / 2)
                      ? null
                      : index + Math.ceil(faqs.length / 2)
                  )
                }
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default FAQKookgroep;
